import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  BellRing,
  Globe,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Network,
  Sun,
  Users,
  X,
} from 'lucide-react';
import { useLogout, type Session } from '../lib/session';
import { useTheme } from '../lib/theme';
import { Button, cn } from './ui';

const NAV_ITEMS = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/users', label: 'Users', icon: Users, end: false },
  { to: '/shared-ips', label: 'Shared IPs', icon: Network, end: false },
  { to: '/countries', label: 'Countries', icon: Globe, end: false },
  { to: '/notifications', label: 'Notifications', icon: BellRing, end: false },
];

/**
 * The app shell: a persistent sidebar from `lg` up, and a slide-over drawer
 * below it. One nav definition drives both, so the two can't drift apart.
 */
export function Layout({ session }: { session: Session }) {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // A tap that navigates should also dismiss the drawer it was tapped in.
  useEffect(() => setNavOpen(false), [location.pathname]);

  return (
    <div className="min-h-dvh bg-canvas">
      <MobileTopBar onOpenNav={() => setNavOpen(true)} />

      {navOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-30 bg-ink/40 backdrop-blur-[2px] lg:hidden"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-line bg-surface',
          'transition-transform duration-200 lg:translate-x-0',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex items-center justify-between px-5 py-4">
          <Brand />
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label="Close navigation"
            className="text-ink-2 lg:hidden"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 space-y-1 px-3 py-2">
          {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-brand-soft text-brand'
                    : 'text-ink-2 hover:bg-raised hover:text-ink',
                )
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>

        <SidebarFooter username={session.username} />
      </aside>

      <main className="lg:pl-64">
        <div className="mx-auto max-w-[1400px] px-4 py-5 sm:px-6 sm:py-7">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand text-sm font-bold text-on-brand">
        W
      </span>
      <span className="text-sm font-semibold tracking-tight text-ink">
        Whatsi Console
      </span>
    </div>
  );
}

function MobileTopBar({ onOpenNav }: { onOpenNav: () => void }) {
  return (
    <header className="sticky top-0 z-20 flex items-center justify-between border-b border-line bg-surface/95 px-4 py-3 backdrop-blur lg:hidden">
      <button
        type="button"
        onClick={onOpenNav}
        aria-label="Open navigation"
        className="text-ink-2"
      >
        <Menu size={22} />
      </button>
      <Brand />
      {/* Balances the menu button so the brand stays optically centred. */}
      <span className="w-[22px]" aria-hidden />
    </header>
  );
}

function SidebarFooter({ username }: { username: string }) {
  const { theme, toggle } = useTheme();
  const logout = useLogout();

  return (
    <div className="space-y-2 border-t border-line px-3 py-3">
      <div className="px-2 text-xs text-ink-3">
        Signed in as <span className="text-ink-2">{username}</span>
      </div>
      <div className="flex gap-2">
        <Button
          variant="ghost"
          onClick={toggle}
          className="flex-1"
          icon={theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        >
          {theme === 'dark' ? 'Light' : 'Dark'}
        </Button>
        <Button
          variant="ghost"
          onClick={() => logout.mutate()}
          loading={logout.isPending}
          className="flex-1"
          icon={<LogOut size={16} />}
        >
          Sign out
        </Button>
      </div>
    </div>
  );
}

/** Page title block, used at the top of every route. */
export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-ink">
          {title}
        </h1>
        {description && <p className="mt-1 text-sm text-ink-2">{description}</p>}
      </div>
      {action}
    </div>
  );
}
